import { HttpErrorResponse } from '@angular/common/http'
import { MAX_BATCH_OPERATION_ITEMS } from '@awork/_shared/constants/batch-operation.constants'
import { TaskCopyBatchLimitError } from '../services/task-service/task-copy-error'

/** Source relationships needed to keep each copy group intact. */
export interface TaskCopyContext {
  id: string
  parentId: string | null
  relatedTaskIds: string[]
}

/**
 * Keeps selected parents, children and dependencies in the same copy request.
 * @param {string[]} taskIds The full selection.
 * @param {TaskCopyContext[]} tasks Fresh task relationships for that selection.
 * @returns {string[][]} Bounded batches of complete related groups.
 */
export function getTaskCopyBatches(taskIds: string[], tasks: TaskCopyContext[]): string[][] {
  const sourceIds = [...new Set([...taskIds, ...tasks.map(task => task.id)])]
  const relatedIds = new Map(sourceIds.map(id => [id, new Set<string>()]))

  if (!taskIds.every(taskId => relatedIds.has(taskId))) {
    throw new HttpErrorResponse({ status: 404, statusText: 'Selected tasks are no longer available.' })
  }

  tasks.forEach(task => {
    const neighbours = [task.parentId, ...task.relatedTaskIds]

    neighbours.forEach(id => {
      if (relatedIds.has(id)) {
        relatedIds.get(task.id).add(id)
        relatedIds.get(id).add(task.id)
      }
    })
  })

  const visited = new Set<string>()
  const batches: string[][] = []
  let batch: string[] = []

  for (const taskId of sourceIds) {
    if (visited.has(taskId)) {
      continue
    }

    const group: string[] = []
    const pending = [taskId]

    while (pending.length) {
      const id = pending.pop()

      if (visited.has(id)) {
        continue
      }

      visited.add(id)
      group.push(id)
      pending.push(...relatedIds.get(id))
    }

    if (group.length > MAX_BATCH_OPERATION_ITEMS) {
      throw new TaskCopyBatchLimitError('Related tasks exceed the copy request limit.')
    }

    if (batch.length + group.length > MAX_BATCH_OPERATION_ITEMS) {
      batches.push(batch)
      batch = []
    }

    batch.push(...group)
  }

  if (batch.length) {
    batches.push(batch)
  }

  return batches
}
